// Shared, side-effect-free constants for the automation operations panel.
// Extracted from AutomationOperationsPanel.vue so the page, its sections and
// the local composables read the exact same contract (query keys, redaction
// list, allowed filter values).

// Automation payloads may carry PII/secrets, so every payload rendered by this
// panel goes through LaisPayloadViewer with an explicit redaction list on top
// of the component defaults. Raw provider payloads/tokens are never printed.
export const REDACTED_KEYS = Object.freeze([
  'password',
  'token',
  'secret',
  'authorization',
  'apikey',
  'api_key',
  'access_token',
  'refresh_token',
  'signature',
  'cpf',
  'cnpj',
  'phone',
  'email',
  'document',
  'bank',
  'account',
  'pix',
  'arguments',
  'backtrace',
  'error_message',
  'payload',
  'body',
  'input',
  'output',
]);

export const QUERY_KEYS = Object.freeze({
  dlqStatus: 'automation_dlq_status',
  dlqPage: 'automation_dlq_page',
  deadLetterId: 'automation_dlq_event_id',
  ruleQuery: 'automation_rule_q',
  ruleEvent: 'automation_event_name',
  ruleStatus: 'automation_rule_status',
  ruleWindow: 'automation_window_hours',
  rulePage: 'automation_rule_page',
  executionId: 'automation_execution_id',
  actionRef: 'automation_action_ref',
});

export const DLQ_STATUS_VALUES = [
  'open',
  'pending',
  'acknowledged',
  'retry_failed',
  'reprocess_requested',
  'skipped',
  'compensated',
  'closed',
  'all',
];

export const RULE_STATUS_VALUES = [
  'all',
  'succeeded',
  'failed',
  'blocked',
  'quarantined',
  'running',
  'pending',
];

export const RECONCILIATION_STATE_OPTIONS = [
  'FAILED',
  'ACCEPTED',
  'SENT',
  'DELIVERED',
  'READ',
];

export const buildDlqStatusOptions = t =>
  DLQ_STATUS_VALUES.map(value => ({
    value,
    label:
      {
        open: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.OPEN'),
        pending: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.PENDING'),
        acknowledged: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.ACKNOWLEDGED'),
        retry_failed: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.RETRY_FAILED'),
        reprocess_requested: t(
          'AUTOMATION.OPERATIONS.DLQ.FILTERS.REPROCESS_REQUESTED'
        ),
        skipped: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.SKIPPED'),
        compensated: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.COMPENSATED'),
        closed: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.CLOSED'),
        all: t('AUTOMATION.OPERATIONS.DLQ.FILTERS.ALL'),
      }[value] || value,
  }));

export const buildRuleStatusOptions = t =>
  RULE_STATUS_VALUES.map(value => ({
    value,
    label:
      {
        all: t('AUTOMATION.OPERATIONS.RULES.FILTERS.ALL'),
        succeeded: t('AUTOMATION.OPERATIONS.RULES.FILTERS.SUCCEEDED'),
        failed: t('AUTOMATION.OPERATIONS.RULES.FILTERS.FAILED'),
        blocked: t('AUTOMATION.OPERATIONS.RULES.FILTERS.BLOCKED'),
        quarantined: t('AUTOMATION.OPERATIONS.RULES.FILTERS.QUARANTINED'),
        running: t('AUTOMATION.OPERATIONS.RULES.FILTERS.RUNNING'),
        pending: t('AUTOMATION.OPERATIONS.RULES.FILTERS.PENDING'),
      }[value] || value,
  }));

export const buildReviewActions = t => [
  {
    name: 'acknowledge',
    label: t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.ACKNOWLEDGE'),
    icon: 'i-lucide-check',
    color: 'teal',
  },
  {
    name: 'retry',
    label: t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.RETRY'),
    icon: 'i-lucide-rotate-ccw',
    color: 'blue',
  },
  {
    name: 'skip',
    label: t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.SKIP'),
    icon: 'i-lucide-skip-forward',
    color: 'amber',
  },
  {
    name: 'compensate',
    label: t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.COMPENSATE'),
    icon: 'i-lucide-wrench',
    color: 'slate',
  },
  {
    name: 'close',
    label: t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.CLOSE'),
    icon: 'i-lucide-circle-x',
    color: 'ruby',
  },
];
